/* eslint-disable max-lines */
import { z } from 'zod';

import {
  DeclarantCustomsQueryFunctionCode,
  DeclarationDocumentsAdditionalFiscalReferenceRole,
  ExternalSystem,
  ImporterStatus,
  LowValuesDeclarantDataSource,
  RelatedResourceIssuerType,
  RelatedResourceStatus,
  SortDirection,
} from '@e-origin/shared';

const DeclarantAddressSchema = z.object({
  street: z.string().optional().nullable(),
  postcode: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  region: z.string().optional().nullable(),
});

const CompanyAddressSchema = z.object({
  street: z.string().optional().nullable(),
  zipCode: z.string().optional().nullable(),
  region: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
});

const DeclarantContactSchema = z.object({
  name: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
});

const InvoiceCounterSchema = z.object({
  year: z.number().optional().nullable(),
  counter: z.number().optional().nullable(),
});

const NlSequenceSchema = z.object({
  year: z.number().optional().nullable(),
  sequence: z.number().optional().nullable(),
});

const DeclarantRepresentativeSchema = z.object({
  identificationNumber: z.string().optional().nullable(),
  name: z.string().optional().nullable(),
  status: z.number().optional().nullable(),
  address: DeclarantAddressSchema.optional().nullable(),
  contactPerson: DeclarantContactSchema.optional().nullable(),
  registration: z.string().optional().nullable(),
});

const DeclarantCustomsAuthSchema = z.object({
  clientId: z.string().optional().nullable(),
  password: z.string().optional().nullable(),
  systemName: z.string().optional().nullable(),
  enterpriseNumber: z.string().optional().nullable(),
  transitProcessIdSequenceNumber: z.string().optional().nullable(),
  secret: z.string().optional().nullable(),
  sendingDelaySeconds: z.number().optional().nullable(),
});

const DeclarantPaymentsFiscalReferenceSchema = z.object({
  role: z.nativeEnum(DeclarationDocumentsAdditionalFiscalReferenceRole).optional().nullable(),
  vatIdentificationNumber: z.string().optional().nullable(),
});

const DeclarantPaymentsSchema = z.object({
  additionalFiscalReference: z.array(DeclarantPaymentsFiscalReferenceSchema).optional().nullable(),
});

const DeclarantRiskCalculationSchema = z.object({
  riskAnalysisEnabled: z.boolean().optional().nullable(),
  amazonFeesCalculationEnabled: z.boolean().optional().nullable(),
  screenshotsEnabled: z.boolean().optional().nullable(),
  aiModuleEnabled: z.boolean().optional().nullable(),
  penaltyValue: z.number().optional().nullable(),
  penaltyDescription: z.number().optional().nullable(),
  penaltyHSCode: z.number().optional().nullable(),
  requestProofEnabled: z.boolean().optional().nullable(),
  vatValidationEnabled: z.boolean().optional().nullable(),
  eoriValidationEnabled: z.boolean().optional().nullable(),
});

const DeclarantValidationSettingsSchema = z.object({
  h7ExcludedCodesValidationEnabled: z.boolean().optional().nullable(),
  IOSSExcludedValidationEnabled: z.boolean().optional().nullable(),
  IOSSBlackList: z.array(z.string()).optional().nullable(),
  VatEoriExcludedValidationEnabled: z.boolean().optional().nullable(),
  VatEoriBlackList: z.array(z.string()).optional().nullable(),
  SameUCRValidationEnabled: z.boolean().optional().nullable(),
  b2cThresholdEnabled: z.boolean().optional().nullable(),
  YListEnabled: z.boolean().optional().nullable(),
  YList: z.array(z.string()).optional().nullable(),
});

const ExternalSystemCustomsSchema = z.object({
  systemName: z.nativeEnum(ExternalSystem).optional().nullable(),
  systemProtocol: z.string().optional().nullable(),
  hostname: z.string().optional().nullable(),
  port: z.number().optional().nullable(),
  username: z.string().optional().nullable(),
  password: z.string().optional().nullable(),
  incomingPath: z.string().optional().nullable(),
  outgoingPath: z.string().optional().nullable(),
  incomingPathBegateFreed: z.string().optional().nullable(),
  incomingPathBegateBlocked: z.string().optional().nullable(),
  incomingPathBegateError: z.string().optional().nullable(),
  outgoingPathBeGate: z.string().optional().nullable(),
});

const DeclarantVatReportSchema = DeclarantRepresentativeSchema.extend({
  excludeProcedure40: z.boolean().optional().nullable(),
  authorizedET14000: z.boolean().optional().nullable(),
  ownerName: z.string().optional().nullable(),
});

const BusinessCardProofSchema = z.object({
  format: z.string().optional().nullable(),
  credential: z.string().optional().nullable(),
  c_nonce: z.string().optional().nullable(),
  c_nonce_expires_in: z.number().optional().nullable(),
});

const LinkedEntitySchema = z.object({
  externalId: z.string().optional().nullable(),
  name: z.string().optional().nullable(),
  identificationNumber: z.string().optional().nullable(),
  invitationCode: z.string().optional().nullable(),
  walletEndpoint: z.string().optional().nullable(),
  proof: BusinessCardProofSchema.optional().nullable(),
});

const CustomsQueryResponseErrorSchema = z.object({
  validationCode: z.string().optional().nullable(),
});

const CustomsQueryAdditionalDocumentSchema = z.object({
  categoryCode: z.string().optional().nullable(),
  typeName: z.string().optional().nullable(),
  amount: z.number().optional().nullable(),
});

const CustomsQueryResponseSchema = z.object({
  issueDate: z.union([z.string(), z.date()]).optional().nullable(),
  additionalDocuments: z.array(CustomsQueryAdditionalDocumentSchema).optional().nullable(),
  error: CustomsQueryResponseErrorSchema.optional().nullable(),
});

const CustomsQueryRequestForGuaranteeSchema = z.object({
  functionCode: z
    .preprocess((v) => (typeof v === 'number' ? String(v) : v), z.nativeEnum(DeclarantCustomsQueryFunctionCode))
    .optional()
    .nullable(),
  declarantIdentificationNumber: z.string().optional().nullable(),
  response: CustomsQueryResponseSchema.optional().nullable(),
});

const CustomsQuerySchema = z.object({
  requestForGuarantee: CustomsQueryRequestForGuaranteeSchema.optional().nullable(),
});

export const RelatedResourceErrorSchema = z.object({
  reason: z.string().optional().nullable(),
});

export const RelatedResourceSchema = z.object({
  fileName: z.string().optional().nullable(),
  mediaType: z.string().optional().nullable(),
  digestMultibase: z.string().optional().nullable(),
  status: z.nativeEnum(RelatedResourceStatus).optional().nullable(),
  status_log: z.array(z.string()).optional().nullable(),
  issuedBy: z.nativeEnum(RelatedResourceIssuerType).optional().nullable(),
  signedUrl: z.string().optional().nullable(),
  issuedAt: z.coerce.date().optional().nullable(),
  errorsStack: z.array(RelatedResourceErrorSchema).optional().nullable(),
});

export const EvaluationSchema = z.object({
  valid: z.boolean().optional().nullable(),
  result: z.string().optional().nullable(),
});

const IdCardSchema = z.object({
  ownerName: z.string().optional().nullable(),
  expirationDate: z.coerce.date().optional().nullable(),
  title: z.string().optional().nullable(),
  registrationNumber: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  relatedResources: z.array(RelatedResourceSchema).optional().nullable(),
  evalExpiration: EvaluationSchema.optional().nullable(),
});

const ProofSchema = z.object({
  format: z.string().optional().nullable(),
  proof: z.string().optional().nullable(),
});

export const EoriSchema = z.object({
  number: z.string().optional().nullable(),
  address: CompanyAddressSchema.optional().nullable(),
  relatedResources: z.array(RelatedResourceSchema).optional().nullable(),
  evalEORI: EvaluationSchema.optional().nullable(),
  skipValidation: z.boolean().optional().nullable(),
});

export const VATSchema = z.object({
  number: z.string().optional().nullable(),
  address: CompanyAddressSchema.optional().nullable(),
  relatedResources: z.array(RelatedResourceSchema).optional().nullable(),
  evalVAT: EvaluationSchema.optional().nullable(),
  skipValidation: z.boolean().optional().nullable(),
});

export const TaxReturnSchema = z.object({
  relatedResources: z.array(RelatedResourceSchema).optional().nullable(),
  evalTaxReturn: EvaluationSchema.optional().nullable(),
});

export const ImportRecordsSchema = z.object({
  relatedResources: z.array(RelatedResourceSchema).optional().nullable(),
  evalImportRecords: EvaluationSchema.optional().nullable(),
});

export const CustomsRepresentativeSchema = z.object({
  companyRegistrationNumber: z.string().optional().nullable(),
  vatNumber: z.string().optional().nullable(),
  customsRegistrationNumber: z.string().optional().nullable(),
  address: CompanyAddressSchema.optional().nullable(),
});

export const VatRepresentativeSchema = z.object({
  vatNumber: z.string().optional().nullable(),
  address: CompanyAddressSchema.optional().nullable(),
});

export const PoaSchema = z.object({
  startingDate: z.coerce.date().optional().nullable(),
  expirationDate: z.coerce.date().optional().nullable(),
  customsRepresentative: CustomsRepresentativeSchema.optional().nullable(),
  relatedResources: z.array(RelatedResourceSchema).optional().nullable(),
});

export const VatGuaranteeSchema = z.object({
  startingDate: z.coerce.date().optional().nullable(),
  expirationDate: z.coerce.date().optional().nullable(),
  vatRepresentative: VatRepresentativeSchema.optional().nullable(),
  relatedResources: z.array(RelatedResourceSchema).optional().nullable(),
});

export const BusinessLicenseSchema = z.object({
  nameEN: z.string().optional().nullable(),
  nameCN: z.string().optional().nullable(),
  registrationNumber: z.string().optional().nullable(),
  bankAccount: z.string().optional().nullable(),
  address: CompanyAddressSchema.optional().nullable(),
  relatedResources: z.array(RelatedResourceSchema).optional().nullable(),
  evalRegistration: EvaluationSchema.optional().nullable(),
  skipValidation: z.boolean().optional().nullable(),
});

const BaseCompanySchema = z.object({
  name: z.string().optional().nullable(),
  idCard: IdCardSchema.optional().nullable(),
  businessLicense: BusinessLicenseSchema.optional().nullable(),
  eori: EoriSchema.optional().nullable(),
  vat: VATSchema.optional().nullable(),
  status: z.nativeEnum(ImporterStatus).optional().nullable(),
  url: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  address: CompanyAddressSchema.optional().nullable(),
  proofs: z.array(ProofSchema).optional().nullable(),
  taxReturn: TaxReturnSchema.optional().nullable(),
  importRecords: ImportRecordsSchema.optional().nullable(),
  poa: PoaSchema.optional().nullable(),
  vatGuarantee: VatGuaranteeSchema.optional().nullable(),
  walletEndpoint: z.string().optional().nullable(),
});

const DefaultBatchValuesSchema = z.object({
  customer: z.any().optional().nullable(),
  template: z.any().optional().nullable(),
});

const DefaultLowValueBatchValuesSchema = z.object({
  b2cLv: DefaultBatchValuesSchema.optional().nullable(),
  b2cHv: DefaultBatchValuesSchema.optional().nullable(),
  b2cH7Excluded: DefaultBatchValuesSchema.optional().nullable(),
});

const DefaultCustomersAndTemplatesSchema = z.object({
  highValues: DefaultBatchValuesSchema.optional().nullable(),
  highValuesExport: DefaultBatchValuesSchema.optional().nullable(),
  lowValues: DefaultLowValueBatchValuesSchema.optional().nullable(),
});

const FunctionalFeaturesSchema = z.object({
  autoB2CSplitLVHV: z.boolean().optional().nullable(),
  lowValuesDeclarantDataSource: z.nativeEnum(LowValuesDeclarantDataSource).optional().nullable(),
  showNewLvReportColumns: z.boolean().optional().nullable(),
  auditTrailEnabled: z.boolean().optional().nullable(),
});

export const DeclarantListItemSchema = z.object({
  _id: z.any(),
  accountName: z.string(),
  name: z.string().optional().nullable(),
  language: z.string().optional().nullable(),
  externalSystemCustoms: ExternalSystemCustomsSchema.optional().nullable(),
  walletEndpoint: z.string().optional().nullable(),
  hasInvoiceTemplate: z.boolean().optional().nullable(),
  invoiceCounter: z.array(InvoiceCounterSchema).optional().nullable(),
  declarationCounter: z.array(InvoiceCounterSchema).optional().nullable(),
  identificationNumber: z.string().optional().nullable(),
  address: DeclarantAddressSchema.optional().nullable(),
  authorisedIdentity: z.string().optional().nullable(),
  contactPerson: DeclarantContactSchema.optional().nullable(),
  representative: DeclarantRepresentativeSchema.optional().nullable(),
  customsAuthentication: DeclarantCustomsAuthSchema.optional().nullable(),
  payments: DeclarantPaymentsSchema.optional().nullable(),
  risksCalculation: DeclarantRiskCalculationSchema.optional().nullable(),
  goodsLocations: z.array(z.any()).optional().nullable(),
  validationSettings: DeclarantValidationSettingsSchema.optional().nullable(),
  users: z.array(z.any()).optional().nullable(),
  deleted: z.boolean().optional().nullable(),
  default: z.boolean().optional().nullable(),
  vatReport: DeclarantVatReportSchema.optional().nullable(),
  nlSequence: z.array(NlSequenceSchema).optional().nullable(),
  linkedSellers: z.array(LinkedEntitySchema).optional().nullable(),
  linkedBrokers: z.array(LinkedEntitySchema).optional().nullable(),
  company: BaseCompanySchema.optional().nullable(),
  customsQuery: CustomsQuerySchema.optional().nullable(),
  defaultCustomersAndTemplates: DefaultCustomersAndTemplatesSchema.optional().nullable(),
  functionalFeatures: FunctionalFeaturesSchema.optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
  virtuals: z
    .object({
      hasExternalSystemCustoms: z.boolean().optional(),
      hasWallet: z.boolean().optional(),
      isPldaDisabled: z.boolean().optional(),
    })
    .optional(),
});

export type DeclarantListItem = z.infer<typeof DeclarantListItemSchema>;

export const CreateDeclarantSchema = z.object({
  accountName: z.string().min(1, { message: 'Account name is required' }),
  name: z.string().optional().nullable(),
  language: z.string().optional().nullable(),
  externalSystemCustoms: ExternalSystemCustomsSchema.optional().nullable(),
  walletEndpoint: z.string().optional().nullable(),
  identificationNumber: z.string().optional().nullable(),
  address: DeclarantAddressSchema.optional().nullable(),
  authorisedIdentity: z.string().optional().nullable(),
  contactPerson: DeclarantContactSchema.optional().nullable(),
  representative: DeclarantRepresentativeSchema.optional().nullable(),
  customsAuthentication: DeclarantCustomsAuthSchema.optional().nullable(),
  payments: DeclarantPaymentsSchema.optional().nullable(),
  risksCalculation: DeclarantRiskCalculationSchema.optional().nullable(),
  validationSettings: DeclarantValidationSettingsSchema.optional().nullable(),
  default: z.boolean().optional().default(false),
  vatReport: DeclarantVatReportSchema.optional().nullable(),
  linkedSellers: z.array(LinkedEntitySchema).optional().nullable(),
  linkedBrokers: z.array(LinkedEntitySchema).optional().nullable(),
  company: BaseCompanySchema.optional().nullable(),
  customsQuery: CustomsQuerySchema.optional().nullable(),
  defaultCustomersAndTemplates: DefaultCustomersAndTemplatesSchema.optional().nullable(),
  functionalFeatures: FunctionalFeaturesSchema.optional().nullable(),
});

export type CreateDeclarant = z.infer<typeof CreateDeclarantSchema>;

export const UpdateDeclarantSchema = z.object({
  _id: z.any(),
  accountName: z.string().optional().nullable(),
  name: z.string().optional().nullable(),
  language: z.string().optional().nullable(),
  externalSystemCustoms: ExternalSystemCustomsSchema.optional().nullable(),
  walletEndpoint: z.string().optional().nullable(),
  identificationNumber: z.string().optional().nullable(),
  address: DeclarantAddressSchema.optional().nullable(),
  authorisedIdentity: z.string().optional().nullable(),
  contactPerson: DeclarantContactSchema.optional().nullable(),
  representative: DeclarantRepresentativeSchema.optional().nullable(),
  customsAuthentication: DeclarantCustomsAuthSchema.optional().nullable(),
  payments: DeclarantPaymentsSchema.optional().nullable(),
  risksCalculation: DeclarantRiskCalculationSchema.optional().nullable(),
  validationSettings: DeclarantValidationSettingsSchema.optional().nullable(),
  deleted: z.boolean().optional(),
  default: z.boolean().optional(),
  vatReport: DeclarantVatReportSchema.optional().nullable(),
  invoiceCounter: z.array(InvoiceCounterSchema).optional().nullable(),
  declarationCounter: z.array(InvoiceCounterSchema).optional().nullable(),
  nlSequence: z.array(NlSequenceSchema).optional().nullable(),
  linkedSellers: z.array(LinkedEntitySchema).optional().nullable(),
  linkedBrokers: z.array(LinkedEntitySchema).optional().nullable(),
  company: BaseCompanySchema.optional().nullable(),
  customsQuery: CustomsQuerySchema.optional().nullable(),
  defaultCustomersAndTemplates: DefaultCustomersAndTemplatesSchema.optional().nullable(),
  functionalFeatures: FunctionalFeaturesSchema.optional().nullable(),
  invoiceCounterReset: z.boolean().optional(),
  declarationCounterReset: z.boolean().optional(),
  nlSequenceReset: z.boolean().optional(),
});

export type UpdateDeclarant = z.infer<typeof UpdateDeclarantSchema>;

export const RetrieveDeclarantsSchema = z.object({
  criteria: z
    .object({
      filters: z.object({ searchQuery: z.string().optional() }).optional(),
      pagination: z.object({ page: z.number().optional(), size: z.number().optional() }).optional(),
      sorting: z.object({ field: z.string().optional(), direction: z.enum([SortDirection.ASC, SortDirection.DESC]).optional() }).optional(),
    })
    .optional(),
});

export type RetrieveDeclarants = z.infer<typeof RetrieveDeclarantsSchema>;

export const RetrieveDeclarantsResponseSchema = z.object({
  data: z.object({
    declarants: z.array(DeclarantListItemSchema),
    totalItems: z.number(),
  }),
});

export type RetrieveDeclarantsResponse = z.infer<typeof RetrieveDeclarantsResponseSchema>;
